import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Product, ProductImage } from "@shared/types/product";

export interface CartItem {
  productId: string;
  _id: string; // Para compatibilidad con el componente Cart
  name: string;
  description?: string;
  quantity: number;
  img: string;
  images?: ProductImage[]; // Actualizado a nueva estructura
  brand?: string;
  unitOfMeasurement?: string;
  linkUrl: string;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  getItemCount: () => number;
  getItemById: (productId: string) => CartItem | undefined;
  getTotalPrice: () => number;
  isEmpty: () => boolean;
  hasItemsWithZeroQuantity: () => boolean;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      totalItems: 0,
      totalPrice: 0,
      isOpen: false,

      addToCart: (product: Product, quantity: number = 1) => {
        const state = get();
        const existingItem = state.items.find(
          (item) => item.productId === product.id
        );

        let updatedItems: CartItem[];

        if (existingItem) {
          // Si ya existe, actualizar cantidad
          updatedItems = state.items.map((item) =>
            item.productId === product.id
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          // Si no existe, agregar nuevo item
          const newItem: CartItem = {
            productId: product.id,
            _id: product.id, // Para compatibilidad
            name: product.name,
            description: product.description || undefined,
            quantity,
            img: product.images?.[0]?.url || "",
            images: product.images || undefined, // Para compatibilidad
            brand: product.brand || undefined,
            unitOfMeasurement: product.unitOfMeasurement || "unidades",
            linkUrl: `/product/${product.slug}`,
          };

          updatedItems = [...state.items, newItem];
        }

        const totalItems = updatedItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        set({
          items: updatedItems,
          totalItems,
        });
      },

      removeFromCart: (productId: string) => {
        const state = get();
        const updatedItems = state.items.filter(
          (item) => item.productId !== productId
        );
        const totalItems = updatedItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        set({
          items: updatedItems,
          totalItems,
        });
      },

      updateQuantity: (productId: string, quantity: number) => {
        // Permitir cantidad 0 sin eliminar el producto
        const state = get();
        const updatedItems = state.items.map((item) =>
          item.productId === productId ? { ...item, quantity: Math.max(0, quantity) } : item
        );
        const totalItems = updatedItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        set({
          items: updatedItems,
          totalItems,
        });
      },

      clearCart: () => {
        set({
          items: [],
          totalItems: 0,
          totalPrice: 0,
        });
      },

      openCart: () => {
        set({ isOpen: true });
      },

      closeCart: () => {
        set({ isOpen: false });
      },

      toggleCart: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },

      getItemCount: () => {
        return get().totalItems;
      },

      getItemById: (productId: string) => {
        return get().items.find((item) => item.productId === productId);
      },

      getTotalPrice: () => {
        return get().totalPrice;
      },

      isEmpty: () => {
        return get().items.length === 0;
      },

      hasItemsWithZeroQuantity: () => {
        return get().items.some((item) => item.quantity === 0);
      },
    }),
    {
      name: "ceramicas-gassull-cart",
      partialize: (state) => ({
        items: state.items,
        totalItems: state.totalItems,
        totalPrice: state.totalPrice,
      }),
    }
  )
);
